import { NavLink, useNavigation, useResolvedPath } from 'react-router-dom';
import { Loader2, PanelLeftClose, PanelLeftOpen } from 'lucide-react';
import { IconButton } from '@/components/common/IconButton';
import { Button } from '@/components/ui/button';
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from '@/components/ui/tooltip';
import { APP_NAME, ROUTES } from '@/constants';
import { useAuthStore } from '@/features/auth';
import { cn } from '@/lib/utils';
import { AppLogo } from './AppLogo';
import { navigationItems, type NavigationItem } from './navigation';

interface AppSidebarProps {
	className?: string;
	/** Called after a link is activated (e.g. to close the mobile drawer). */
	onNavigate?: () => void;
	/** Icon-only rail; labels move into tooltips. */
	collapsed?: boolean;
	/** Shows the collapse/expand button when provided (desktop rail only). */
	onToggleCollapsed?: () => void;
}

/** Primary navigation, shared by the desktop rail and the mobile drawer. */
export function AppSidebar({
	className,
	onNavigate,
	collapsed = false,
	onToggleCollapsed,
}: AppSidebarProps) {
	const role = useAuthStore((state) => state.user?.role);
	const items = navigationItems.filter(
		(item) => !item.roles || (role !== undefined && item.roles.includes(role)),
	);

	return (
		<aside
			className={cn(
				'bg-background flex shrink-0 flex-col border-r transition-[width] duration-200 motion-reduce:transition-none',
				collapsed ? 'w-16' : 'w-64',
				className,
			)}
		>
			<div
				className={cn(
					'flex h-16 items-center border-b',
					collapsed ? 'justify-center' : 'px-4',
				)}
			>
				<NavLink
					to={ROUTES.home}
					className="flex min-w-0 items-center gap-2.5 text-base font-semibold"
					onClick={onNavigate}
				>
					<AppLogo />
					<span className={cn('truncate', collapsed && 'sr-only')}>
						{APP_NAME}
					</span>
				</NavLink>
			</div>

			<nav
				className={cn(
					'flex-1 space-y-1 overflow-x-hidden overflow-y-auto',
					collapsed ? 'p-2' : 'p-3',
				)}
				aria-label="Main"
			>
				{items.map((item) => (
					<SidebarLink
						key={item.to}
						item={item}
						collapsed={collapsed}
						onNavigate={onNavigate}
					/>
				))}
			</nav>

			{onToggleCollapsed && (
				<div
					className={cn(
						'border-t',
						collapsed ? 'flex justify-center p-2' : 'p-3',
					)}
				>
					{collapsed ? (
						<IconButton
							label="Expand sidebar"
							variant="ghost"
							onClick={onToggleCollapsed}
						>
							<PanelLeftOpen />
						</IconButton>
					) : (
						<Button
							variant="ghost"
							className="text-muted-foreground w-full justify-start gap-3 px-3"
							onClick={onToggleCollapsed}
						>
							<PanelLeftClose />
							Collapse sidebar
						</Button>
					)}
				</div>
			)}
		</aside>
	);
}

interface SidebarLinkProps {
	item: NavigationItem;
	collapsed: boolean;
	onNavigate?: () => void;
}

function SidebarLink({
	item: { label, to, icon: Icon, end },
	collapsed,
	onNavigate,
}: SidebarLinkProps) {
	const navigation = useNavigation();
	const { pathname } = useResolvedPath(to);
	// Its page is loading (a slow chunk): show it, and ignore repeated clicks.
	const loading =
		navigation.state === 'loading' && navigation.location.pathname === pathname;

	// A string className (not NavLink's function form) so the tooltip's Slot can
	// merge it; the active state comes from the `aria-current="page"` NavLink sets.
	const link = (
		<NavLink
			to={to}
			end={end}
			aria-busy={loading || undefined}
			onClick={(event) => {
				if (loading) {
					event.preventDefault();
					return;
				}
				onNavigate?.();
			}}
			className={cn(
				'text-muted-foreground hover:bg-accent hover:text-accent-foreground aria-[current=page]:bg-accent aria-[current=page]:text-accent-foreground flex items-center gap-3 rounded-md py-2 text-sm font-medium transition-colors',
				collapsed ? 'justify-center px-0' : 'px-3',
				loading && 'bg-accent/60 text-accent-foreground cursor-progress',
			)}
		>
			{loading ? (
				<Loader2 className="size-4 shrink-0 animate-spin" aria-hidden="true" />
			) : (
				<Icon className="size-4 shrink-0" />
			)}
			<span className={cn('truncate', collapsed && 'sr-only')}>{label}</span>
		</NavLink>
	);

	if (!collapsed) return link;

	return (
		<Tooltip>
			<TooltipTrigger asChild>{link}</TooltipTrigger>
			<TooltipContent side="right">{label}</TooltipContent>
		</Tooltip>
	);
}
